import * as React from "react";
import {Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import {ValuableItem, ValuableItemTypeEnum, ValuableItemRequest, ICustomerPortalService, Types, ValuableItemCoverLocation} from "@falcon/services";
import {lazyInject} from "@athena-portal/core";
import {IWithRouterProps, withRouter} from "@athena-portal/components";

interface IProps {
    show: boolean
    hide: any
    onConfirm: any
    item: ValuableItem
    itemWordings: any
    interactionId: string
    totalValueOfItems: number
    insuredValue: number
}

interface IState {
    item: ValuableItem
    isValueValid: boolean
    isFormValid: boolean
    showMobileProtectionAlert: boolean
    isInsuredValueLargerSeventeenPercents: boolean
}

export class ValuableItemModal extends React.Component<IProps & IWithRouterProps, IState> {

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    private totalValueOfItems: number = this.props.totalValueOfItems;

    constructor(props: IProps & IWithRouterProps) {
        super(props);

        this.state = {
            item: {...this.props.item},
            isValueValid: true,
            isFormValid: false,
            showMobileProtectionAlert: true,
            isInsuredValueLargerSeventeenPercents: false
        };
    }

    public render() {
        return (
            <Modal className="my-2 cover-amount-modal" isOpen={this.props.show} toggle={this.handleClose}>
                <ModalHeader toggle={this.handleClose} close={<button className="btn-close" onClick={this.props.hide}/>}>
                    <div className="mb-4 text-blue-2 font-weight-medium">Edit item</div>
                </ModalHeader>
                <ModalBody className="py-0 fs-16 font-weight-extra-light">
                    {this.state.item.valuableItemType === ValuableItemTypeEnum.MOBILE && this.state.showMobileProtectionAlert && this.renderMobileProtectionAlert()}
                    {this.renderDescriptionInput()}
                    {this.renderSerialNumberInput()}
                    {this.renderValueInput()}
                    {this.state.isInsuredValueLargerSeventeenPercents && this.renderValuableItemsTotalPriceAlert()}
                    {this.renderCoverLocationRadio()}
                </ModalBody>
                <ModalFooter className="d-block">
                    <button type="button"
                            className={`btn btn-primary btn-next w-100 ${(!this.state.isFormValid || this.state.isInsuredValueLargerSeventeenPercents) && "btn-disabled"}`}
                            onClick={e => this.state.isFormValid ? this.handleConfirm() : e.preventDefault}>
                        Update
                    </button>
                </ModalFooter>
            </Modal>
        );
    }

    private renderValuableItemsTotalPriceAlert = (): React.ReactNode => {
        return (
            <div className="d-flex align-items-start bg-white-blue rounded p-3 mb-3">
                <img className="d-block mr-3 mt-1" src="/img/Flow-attention.svg" alt="warning"/>
                <div>
                    Looks like you've used up most of your contents limit on specified items, you may want to review your total contents limit to ensure you're covered for all of your possessions in the event of a claim.
                    <div className="text-decoration-underline cursor-pointer text-blue-1" onClick={() => this.setState({isInsuredValueLargerSeventeenPercents: false})}>
                        <b>Ok, I understand</b>
                    </div>
                </div>
            </div>
        )
    };

    private renderDescriptionInput = (): React.ReactNode => {
        return (
            <div className="form-group">
                <label htmlFor="description">
                    <span className="font-weight-extra-light">{this.props.itemWordings.description}</span>
                </label>
                <div className="input-group w-100">
                    <input type="text"
                           id="description"
                           className="form-control"
                           value={this.state.item.description}
                           maxLength={100}
                           onChange={this.handleDescriptionChange}/>
                </div>
            </div>
        )
    };

    private renderSerialNumberInput = (): React.ReactNode => {
        return (
            <div className="form-group">
                <label htmlFor="serialNumber">
                    <span className="font-weight-extra-light">{this.props.itemWordings.serialNumber}</span>
                </label>
                <div className="input-group">
                    <input type="text"
                           id="serialNumber"
                           className="form-control"
                           value={this.state.item.serialNumber}
                           maxLength={25}
                           onChange={this.handleSerialNumberChange}/>
                </div>
            </div>
        )
    };

    private renderValueInput = (): React.ReactNode => {
        return (
            <div className="form-group">
                <label htmlFor="value">
                    How much would it cost to replace this item brand new?<br/>
                    <span className="font-weight-extra-light">(This is maximum we can pay if you make a claim)</span>
                </label>
                <div className="input-group d-flex align-items-center">
                    <span className="fs-22 font-weight-bold">£</span>
                    <input type="text"
                           id="value"
                           className="form-control w-25 mx-2"
                           value={this.state.item.value || ""}
                           onBlur={() => this.validateValue(true)}
                           onChange={this.handleValueChange}/>
                    <span> (maximum £5,000)</span>
                </div>
                {!this.state.isValueValid && <div className="text-danger fs-14 font-weight-medium mt-2 mt-md-0">Enter value between £1 and £5000</div>}
            </div>
        )
    };

    private renderCoverLocationRadio = (): React.ReactNode => {
        return (
            <div className="form-group mt-4">
                <label>
                    <span className="font-weight-extra-light">Do you want to</span> cover this item outside the home?
                </label>
                <div className="input-group width-450" role="group">
                    <div className="custom-control custom-radio">
                        <input className="custom-control-input"
                               type="radio"
                               name="specified-items-radio"
                               id="location-outside"
                               value={ValuableItemCoverLocation.ANYWHERE}
                               checked={this.state.item.coverLocation === ValuableItemCoverLocation.ANYWHERE}
                               onChange={this.handleLocationChange}/>
                        <label className="custom-control-label" htmlFor="location-outside">
                            Yes
                        </label>
                    </div>
                    <div className="custom-control custom-radio">
                        <input className="custom-control-input"
                               type="radio"
                               name="specified"
                               id="location-inside"
                               value={ValuableItemCoverLocation.HOME}
                               checked={this.state.item.coverLocation === ValuableItemCoverLocation.HOME}
                               onChange={this.handleLocationChange}/>
                        <label className="custom-control-label" htmlFor="location-inside">
                            No
                        </label>
                    </div>
                </div>
            </div>
        )
    };

    private renderMobileProtectionAlert = (): React.ReactNode => {
        return (
            <div className="d-flex align-items-start bg-white-blue rounded p-3 mb-3">
                <img className="d-block mr-3 mt-1" src="/img/Flow-attention.svg" alt="warning"/>
                <div>
                    Please first <b>check if you're already protected</b>, e.g. if you have an iPhone verify if you signed up for AppleCare+
                    <div className="text-decoration-underline cursor-pointer text-blue-1" onClick={() => this.setState({showMobileProtectionAlert: false})}>
                        <b>Ok, thanks</b>
                    </div>
                </div>
            </div>
        )
    };

    private handleDescriptionChange = (event: any) => {
        const item: ValuableItem = this.state.item;
        item.description = event.target.value;
        this.setState({item});
        this.validate();
    };

    private handleSerialNumberChange = (event: any) => {
        const item: ValuableItem = this.state.item;
        item.serialNumber = event.target.value.toUpperCase();
        this.setState({item});
        this.validate();
    };

    private handleValueChange = (event: any) => {
        const item: ValuableItem = this.state.item;
        item.value = event.target.value;
        this.setState({item});
        this.validate();
    };

    private handleLocationChange = (event: any) => {
        const item: ValuableItem = this.state.item;
        item.coverLocation = event.target.value;
        this.setState({item});
        this.validate();
    };

    private validate = (): boolean => {
        const isDescriptionValid: boolean = Boolean(this.state.item.description);
        const isLocationValid: boolean = Boolean(this.state.item.coverLocation);
        const isValueValid: boolean = this.validateValue(false);

        if (isDescriptionValid && isValueValid && isLocationValid) {
            this.setState({isFormValid: true});
            return true;
        } else {
            this.setState({isFormValid: false});
            return false
        }
    };

    private validateValue = (updateFieldValidation: boolean): boolean => {
        const isInsuredValueLargerSeventeenPercents: boolean = Boolean(this.state.item.value && (this.totalValueOfItems + Number(this.state.item.value)) > this.props.insuredValue * 0.7)
        const isValueValid: boolean = Boolean(this.state.item.value && this.state.item.value >= 1 && this.state.item.value <= 5000);
        updateFieldValidation && this.setState({isValueValid, isInsuredValueLargerSeventeenPercents});
        return isValueValid;
    };

    private handleConfirm = (): void => {
        if (this.validate()) {
            if (this.calculationRequired()) {
                window.sessionStorage.removeItem("itemToProcess");
                window.sessionStorage.setItem("itemToProcess", JSON.stringify(this.state.item));
                this.props.navigate(`/journey/show/product/RentersInsurance/process/mtaEditItem/preset/${this.props.interactionId}`);
            } else {
                const request: ValuableItemRequest = new ValuableItemRequest();
                request.id = this.state.item.id;
                request.description = this.state.item.description;
                request.serialNumber = this.state.item.serialNumber;
                request.coverLocation = this.state.item.coverLocation;
                this.customerPortalService.updateValuableItem(request).subscribe();

                this.props.onConfirm(this.state.item);
            }
        }
    };

    private handleClose = (): void => {
        this.props.hide(this.props.item);
    };

    private calculationRequired(): boolean {
        const isValueChanged: boolean = this.props.item.value.toString() !== this.state.item.value.toString();
        const isLocationChanged: boolean = this.props.item.coverLocation !== this.state.item.coverLocation;
        return isValueChanged || isLocationChanged;
    }
}

export default withRouter<IProps>(ValuableItemModal)
